import { parseScheduleWindows, type TimeWindow, toTimeParts } from '@provision/sidekick-utils';
import { getConditionGroupLevel } from 'src/components/ConditionsBuilder/condition-fields';
import {
  ConditionType,
  DataConditionOperator,
} from 'src/graphql/entities/behavior/behavior-config/behavior-config.types';
import type {
  CustomRuleType,
  SelectorValue,
} from 'src/graphql/entities/behavior/behavior.types';
import { isCompleteConditionRule } from 'src/utils/query-builder-conditions';

const VALUELESS_OPERATORS = new Set([
  DataConditionOperator.Exists,
  DataConditionOperator.DoesNotExist,
  DataConditionOperator.Empty,
  DataConditionOperator.NotEmpty,
  DataConditionOperator.True,
  DataConditionOperator.False,
]);

/** A condition group as the rules form holds it: rules and nested groups. */
interface ConditionsQuery {
  rules?: readonly unknown[];
}

export const INCOMPLETE_CONDITION_MESSAGE =
  'Pick an attribute and a value for every condition';
export const EMPTY_SCHEDULE_MESSAGE =
  'Select at least one day for each Custom schedule condition';
export const INVERTED_RANGE_MESSAGE =
  'Each Custom schedule needs an end time after its start time';

/**
 * The conditions' save check: every Custom schedule row needs a day, and an end time after its
 * start time. Either gap leaves a schedule that contains no moment, which never matches as Within
 * and always does as Outside.
 */
export function validateScheduleConditions(group: ConditionsQuery): true | string {
  const windowsOfEachSchedule = collectScheduleWindows(group);

  if (windowsOfEachSchedule.some((windows) => windows.length === 0)) {
    return EMPTY_SCHEDULE_MESSAGE;
  }

  if (windowsOfEachSchedule.some((windows) => windows.some(isInvertedRange))) {
    return INVERTED_RANGE_MESSAGE;
  }

  return true;
}

/**
 * The conditions' completeness check, at every level: a condition needs its attribute picked and a
 * value, unless its operator takes none. A half-filled row would be saved without an attribute.
 */
export function validateCompleteConditions(group: ConditionsQuery): true | string {
  return hasIncompleteCondition(group) ? INCOMPLETE_CONDITION_MESSAGE : true;
}

export function groupLevelMessage(maxLevel: number): string {
  return `This event allows at most ${maxLevel} ${maxLevel === 1 ? 'level' : 'levels'} of groups`;
}

/**
 * The conditions' depth check for events whose evaluator reads only so many levels of groups:
 * the scheduled evaluator drops a whole rule with deeper groups, so it would never run.
 */
export function validateConditionGroupLevel(
  group: ConditionsQuery,
  maxLevel: number | null | undefined,
): true | string {
  if (
    maxLevel === null ||
    maxLevel === undefined ||
    getConditionGroupLevel(group) <= maxLevel
  ) {
    return true;
  }

  return groupLevelMessage(maxLevel);
}

/** Whether a window's end is not after its start: the time pickers let a typed time through. */
export function isInvertedRange({ timeFrom, timeTo }: TimeWindow): boolean {
  return toMinutes(timeTo) <= toMinutes(timeFrom);
}

function collectScheduleWindows(group: ConditionsQuery): TimeWindow[][] {
  return (group?.rules ?? []).flatMap((rule) => {
    if (isConditionsQuery(rule)) {
      return collectScheduleWindows(rule);
    }

    const { field, value } = rule as CustomRuleType;
    const isSchedule =
      (field as unknown as SelectorValue | undefined)?.conditionType ===
      ConditionType.CustomSchedule;
    return isSchedule ? [parseScheduleWindows(value)] : [];
  });
}

function hasIncompleteCondition(group: ConditionsQuery): boolean {
  return (group?.rules ?? []).some((rule) =>
    isConditionsQuery(rule)
      ? hasIncompleteCondition(rule)
      : !isCompleteRule(rule as CustomRuleType),
  );
}

// a picked attribute is stored as its whole field object; a plain name is one the mappers cannot save
function isCompleteRule(rule: CustomRuleType): boolean {
  return (
    typeof rule.field === 'object' &&
    rule.field !== null &&
    isCompleteConditionRule(rule, (operator) =>
      VALUELESS_OPERATORS.has(operator as DataConditionOperator),
    )
  );
}

function isConditionsQuery(rule: unknown): rule is ConditionsQuery {
  return typeof rule === 'object' && rule !== null && 'rules' in rule;
}

// `parseScheduleWindows` has already checked both times are clock times
function toMinutes(time: string): number {
  const parts = toTimeParts(time);
  return parts ? parts.hour * 60 + parts.minute : 0;
}
